import ScrollPane from "../../../../../shared/lib/html5-software-library/src/ranj/components/scrolling/ScrollPane";
import CacheUtil from "../../../../../shared/lib/html5-software-library/src/ranj/util/CacheUtil";
import AbstractView from "../../../../../shared/src/ranj/equalture/view/AbstractView";
import PuzzleModel from "../model/PuzzleModel";
import GameElementSkinFactory from "./GameElementSkinFactory";
import PuzzleItemView from "./PuzzleItemView";
import PuzzleScrollPane from "./PuzzleScrollPane";

export default class PuzzleView extends AbstractView {

    /**
     * 
     * @param {createjs.Container} target 
     * @param {PuzzleModel} puzzleModel 
     * @param {GameElementSkinFactory} elementFactory 
     * @param {createjs.Rectangle} viewport 
     */
    constructor(target, puzzleModel, elementFactory, viewport) {
        super(target);
        this._model = puzzleModel;
        this._elementFactory = elementFactory;
        this._viewport = viewport;
        this._build();
        if (this._model.position != null) {
            this._setPosition(this._model.position);
        } else {
            this._centerLocation();
        }
    }

    destroy() {
        super.destroy();
        this.stopDragNudge();
        this._scrollpane.destroy();
        this._itemViews.forEach(itemView => { itemView.destroy(); });
    }

    get target() {
        return this._target;
    }

    set viewport(rectangle) {
        if (rectangle != null) {
            this._viewport = rectangle;
            if (this._scrollpane) {
                this._scrollpane.setViewPort(this._viewport);
                this._centerLocation();
            }
        }
    }

    _centerLocation() {
        const b = this._locationBounds, vp = this._viewport;
        this._scrollpane.setScrollPosition(-(b.width - vp.width) / 2, -(b.height - vp.height) / 2);
    }

    /**
     * 
     * @param {{x:Number, y:Number}} pos 
     */
    _setPosition(pos) {
        this._scrollpane.setScrollPosition(pos.x, pos.y);
    }

    _build() {
        const locationSkin = this._target.addChild(this._elementFactory.getLocation(this._model.location));
        locationSkin.gotoAndStop(0);
        this._locationSkin = locationSkin;
        const b = this._locationBounds = this._elementFactory.getLocationBounds(this._model.location);
        const vp = this._viewport ? this._viewport.clone() : new createjs.Rectangle(0, 0, 100, 100);

        //ScrollPane ignores x and y values of viewport and content bounds! So make sure both viewport and bounds have TL at 0,0.
        this._scrollpane = new PuzzleScrollPane(vp, null, null, locationSkin, null, true, false, 0, this._target.stage);
        this._scrollpane.setClickMargin(12);
        this._scrollpane.setBounds(b.x, b.y, b.width, b.height);
        this._scrollpane.addEventListener(ScrollPane.SCROLL_STARTED, this._handleScrollStarted.bind(this));
        this._scrollpane.addEventListener(ScrollPane.SCROLL_POSITION_UPDATE, this._handleScrollPositionUpdate.bind(this));

        /**@type {Array<PuzzleItemView} */
        this._itemViews = [];
        this._model.slots.forEach((itemIndex, index) => {
            const target = locationSkin["slot" + index];
            const itemView = new PuzzleItemView(target, true);
            itemView.content = itemIndex != null ? this._elementFactory.getItem(itemIndex) : null;
            this._itemViews[index] = itemView;
            target.addEventListener("click", this._buildClickHandler(index));
            //Blinking disabled for performance
            //itemView.scheduleBlink();
        });
        
        for (let i=1;i<99;i++) {
            const layerSkin = locationSkin["layer" + i];
            if (!layerSkin) {
                break;
            }
            CacheUtil.cache(layerSkin);
            layerSkin.removeAllChildren();
        }        
    }
    

    _buildClickHandler(slotIndex) {
        return e => {
            if (this.enabled) {
                this.dispatchEvent(PuzzleView.SELECT, slotIndex);
            }
        }
    }

    doMismatch(slotIndex) {
        const itemView = this._itemViews[slotIndex];
        itemView.hop();
    }

    doMatch(slotIndex) {
        const itemView = this._itemViews[slotIndex];
        itemView.disappear();
    }



    _handleScrollStarted() {
        this.stopDragNudge();
    }
    _handleScrollPositionUpdate({data}) {
        //Store in model (for savegame)
        if (data.dragging) {
            this._model.dragDistance += data.offset;
        }
        this._model.position = { x: Math.round(this._scrollpane.getX()), y: Math.round(this._scrollpane.getY()) };
        this.updateCulling();
    }

    updateCulling() {
        const r = this._scrollpane.getVisibleArea();
        //Padding based on largest bird graphic
        r.pad(20, 150, 240, 150);
        this._itemViews.forEach(itemView => {
            const target = itemView.target;
            itemView.show(r.contains(target.x, target.y));
        });
    }

    animateOut(duration = 200, delay = 0, callback = null) {
        const props = {};
        if (callback != null) {
            props.onComplete = callback;
        }
        createjs.Tween.get(this._target, props).wait(delay).to({ alpha: 0 }, duration, createjs.Ease.quadIn);
    }

    animateIn(duration = 200, delay = 0, callback = null) {
        const props = {};
        if (callback != null) {
            props.onComplete = callback;
        }
        this._target.alpha = 0;
        createjs.Tween.get(this._target, props).wait(delay).to({ alpha: 1 }, duration, createjs.Ease.quadOut);
    }

    doDragNudge(dragNudgeSkin) {
        const dx = 250, dy = 0;
        const wait = 0;
        const ox = this._scrollpane.getX();
        const oy = this._scrollpane.getY();
        const proxy = { x: ox, y: oy };
        const onChange = _ => {
            this._scrollpane.setScrollPosition(proxy.x, proxy.y);
        }
        this._dragNudgeTween = createjs.Tween.get(proxy, { onChange: onChange, onComplete: this.stopDragNudge.bind(this) })
            .wait(300)
            .to({ x: ox + dx, y: oy + dy }, 600, createjs.Ease.quadInOut)
            .wait(wait)
            .to({ x: ox, y: oy }, 600, createjs.Ease.quadInOut)
            .wait(wait)
            .to({ x: ox - dx, y: oy - dy }, 600, createjs.Ease.quadInOut)
            .wait(wait)
            .to({ x: ox, y: oy }, 600, createjs.Ease.quadInOut)
            .wait(200)
            .call(_ => {                
                createjs.Tween.get(dragNudgeSkin).to({ alpha: 0 }, 200);
            })
            .wait(200);

        //Add hand image
        this._dragNudgeSkin = this._locationSkin.addChild(dragNudgeSkin);
        dragNudgeSkin.x = -ox + this._viewport.width / 2;
        dragNudgeSkin.y = -oy + this._viewport.height / 2;
        dragNudgeSkin.alpha = 0;
        createjs.Tween.get(dragNudgeSkin).to({ alpha: 1 }, 200);
    }

    stopDragNudge() {
        if (this._dragNudgeTween) {
            createjs.Tween.removeTweens(this._dragNudgeTween.target);
            this._dragNudgeTween = null;
            this._locationSkin.removeChild(this._dragNudgeSkin);
            this._dragNudgeSkin = null;
        }
    }

    set enabled(bool) {
        this._enabled = bool;
        this._scrollpane.dragEnabled = bool;
    }

    get enabled() {
        return this._enabled;
    }
}

PuzzleView.SELECT = "PuzzleView.SELECT";
PuzzleView.DRAG = "PuzzleView.DRAG";